/* eslint no-underscore-dangle: "off"*/

import moment from 'moment';

const MILLISECONDS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;

class Shift {
  constructor(rule) {
    this.rule = rule;
    this._startsAt = moment(this.rule.options.dtstart).utc();
    this._startTime = (this._startsAt.hours() * MINUTES_PER_HOUR) + this._startsAt.minutes();
  }

  get count() { return Number(this.rule.options.count); }

  get endsOn() {
    const endsOn = this.endsOnFromUntil || this.endsOnFromCount;
    return (endsOn && endsOn.startOf('day'));
  }

  get endsOnFromCount() { return null; }

  get endsOnFromUntil() { return this.rule.options.until && moment(this.rule.options.until).utc(); }

  get endTime() { return this.startTime + (this.rule.duration / MILLISECONDS_PER_MINUTE); }

  get freq() { return this.rule.options.freq; }

  get interval() { return this.rule.options.interval || 1; }

  get minutesPastEndOfDay() { return this.endTime - MINUTES_PER_DAY; }

  get startsOn() { return moment(this._startsAt).startOf('day'); }

  get startTime() { return this._startTime; }

  endMinutesWithinRange(start, end) {
    return this.endTime > start && this.endTime <= end;
  }

  finalDateAfter(date) {
    return !this.endsOn || this.endsOn >= date;
  }

  rulesForMinutesAfterMidnight() {
    return Object.assign(this.rule, {
      options: Object.assign(this.rule.options, { dtstart: this.startsOn.add(1, 'days').startOf('day') }),
      duration: this.minutesPastEndOfDay * MILLISECONDS_PER_MINUTE,
    });
  }

  startTimeWithinRange(start, end) {
    return this.startTime >= start && this.startTime < end;
  }

  surroundsDate(date) {
    return date >= this.startsOn && this.finalDateAfter(date);
  }
}

class ShiftByDay extends Shift {
  get endsOnFromCount() { return this.count ? this.startsOn.add((this.count - 1) * this.interval, 'days') : null; }

  canFallOnDate(date) {
    return this.surroundsDate(date) && this.startsOn.diff(date, 'days') % this.interval === 0;
  }

  hasCollidingIntervalWithShift(otherShift) {
    const diff = this.startsOn.diff(otherShift.startsOn, 'days');
    return diff % this.interval === 0 || diff % otherShift.interval === 0;
  }

  incrementDate(date) {
    date.add(this.interval, 'days');
  }
}

class ShiftByWeekday extends Shift {
  constructor(rule) {
    super(rule);
    this.weekdays = this.rule.options.byweekday.map(day => day + 1);
    this.incrementStartDateToFirstOccurrence();
  }

  get endsOnFromCount() {
    if (!this.count) { return null; }
    const datetime = this.startsOn;
    [...Array(this.count - 1)].forEach(() => this.incrementDate(datetime));
    return datetime;
  }

  canFallOnDate(date) {
    return this.surroundsDate(date)
      && this.dateInWeekdays(date)
      && this.startsOn.diff(date, 'week') % this.interval === 0;
  }

  dateInWeekdays(date) {
    return this.weekdays.includes(date.isoWeekday());
  }

  hasCollidingIntervalWithShift(otherShift) {
    const diff = this.startsOn.diff(otherShift.startsOn, 'days');
    return diff % this.interval === 0 || diff % otherShift.interval === 0;
  }

  incrementDate(date) {
    if (date.isoWeekday() === 6) { date.add(this.interval - 1, 'weeks'); }
    date.add(1, 'days');
    while (!this.dateInWeekdays(date)) {
      if (date.isoWeekday() === 6) { date.add(this.interval - 1, 'weeks'); }
      date.add(1, 'days');
    }
  }

  incrementStartDateToFirstOccurrence() {
    while (!this.dateInWeekdays(this._startsAt)) { this._startsAt.add(1, 'days'); }
  }
}

class ShiftByPosition extends Shift {
  constructor(rule) {
    super(rule);
    this.weekdays = this.rule.options.byweekday.map(day => day + 1);
    this.incrementStartDateToFirstOccurrence();
  }

  get endsOnFromCount() {
    if (!this.count) { return null; }
    const date = moment(this.startsOn.add(this.count - 1, 'months'));
    this.setDateForMonth(date);
    return date;
  }

  get setpos() { return this.rule.options.bysetpos && this.rule.options.bysetpos[0]; }

  get weeks() {
    if (this.setpos === -1) { return [4, 5]; }
    const averageWeekNumber = this.setpos * this.weekdays.length / 7;
    return [Math.floor(averageWeekNumber), Math.ceil(averageWeekNumber)];
  }

  canFallOnDate(date) {
    const validDate = moment(date);
    this.setDateForMonth(validDate);
    return this.surroundsDate(date) && validDate.isSame(date) && this.dateHasValidInterval(date);
  }

  dateHasValidInterval(date) {
    const diff = this.startsOn.diff(date, 'months');
    return diff % this.interval === 0;
  }

  dateInWeekdays(date) {
    return this.weekdays.includes(date.isoWeekday());
  }

  hasCollidingIntervalWithShift(otherShift) {
    const diff = this.startsOn.diff(otherShift.startsOn, 'days');
    return diff % this.interval === 0 || diff % otherShift.interval === 0;
  }

  incrementDate(date, times) {
    date.add(times || this.interval, 'months');
    this.setDateForMonth(date);
  }

  incrementStartDateToFirstOccurrence() {
    const date = moment(this.startsOn);
    this.setDateForMonth(date);
    if (date < this.startsOn) { this.incrementDate(date, 1); }
    this._startsAt = date;
  }

  setDateForMonth(date) {
    if (this.setpos > 0) {
      this.setDateForMonthFromStart(date);
    } else {
      this.setDateForMonthFromEnd(date);
    }
  }

  setDateForMonthFromEnd(date) {
    date.endOf('month').startOf('day');
    while (!this.dateInWeekdays(date)) { date.subtract(1, 'day'); }
  }

  setDateForMonthFromStart(date) {
    date.startOf('month');
    let count = this.dateInWeekdays(date) ? 1 : 0;
    while (count < this.setpos) {
      date.add(1, 'days');
      if (this.dateInWeekdays(date)) { count += 1; }
    }
  }
}

class ShiftByMonthDay extends Shift {
  constructor(rule) {
    super(rule);

    this.incrementStartDateToFirstOccurrence();
    this.occurrences = [this.startsOn];
  }

  get endsOnFromCount() {
    if (!this.count) { return null; }
    if (this.monthday <= 28) { return this.startsOn.add((this.count - 1) * this.interval, 'months'); }
    while (this.occurrences.length < this.count) {
      this.occurrences.push(this.incrementDate(this.lastOccurrence));
    }

    return this.lastOccurrence;
  }

  get lastOccurrence() { return moment(this.occurrences[this.occurrences.length - 1]); }

  get monthday() { return this.rule.options.bymonthday[0]; }

  get monthdays() { return this.monthday > 0 ? [this.monthday] : [28, 29, 30, 31]; }

  canFallOnDate(date) {
    return this.surroundsDate(date) && date.date() === this.dayForMonth(date);
  }

  dayForMonth(date) {
    return this.monthday > 0 ? this.monthday : moment(date).endOf('month').date();
  }

  hasCollidingIntervalWithShift(otherShift) {
    const diff = this.startsOn.diff(otherShift.startsOn, 'months');
    return diff % this.interval === 0 || diff % otherShift.interval === 0;
  }

  incrementDate(date) {
    if (date.date() === this.dayForMonth(date)) { date.add(this.interval, 'months'); }
    while (date.date() !== this.dayForMonth(date)) { date.add(1, 'days'); }
    return date;
  }

  incrementStartDateToFirstOccurrence() {
    while (this._startsAt.date() !== this.dayForMonth(this._startsAt)) { this._startsAt.add(1, 'days'); }
  }
}

class ShiftByWeekNumber extends Shift {
  constructor(rule) {
    super(rule);
    if (!this.occurrenceInMonth(this._startsAt).isSame(this.startsOn)) {
      this.incrementDate(this._startsAt);
    }
  }

  get monthdays() {
    switch (this.week) {
      case 1: return [1, 2, 3, 4, 5, 6, 7];
      case 2: return [8, 9, 10, 11, 12, 13, 14];
      case 3: return [15, 16, 17, 18, 19, 20, 21];
      default: return [22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
    }
  }

  get weekday() { return this.rule.options.bynweekday[0][0] + 1; }

  get weekdays() { return [this.weekday]; }

  get week() { return this.rule.options.bynweekday[0][1]; }

  get weeks() { return [this.week]; }

  canFallOnDate(date) {
    return this.surroundsDate(date) && this.occurrenceInMonth(date).isSame(date);
  }

  occurrenceInMonth(passedDate) {
    if (this.week > 0) { return this.occurrenceInMonthFromStart((passedDate)); }
    return this.occurrenceInMonthFromEnd(passedDate);
  }

  occurrenceInMonthFromEnd(passedDate) {
    const date = moment(passedDate).endOf('month');
    while (this.weekday !== date.isoWeekday()) { date.subtract(1, 'days'); }
    return date.startOf('day');
  }

  occurrenceInMonthFromStart(passedDate) {
    const date = moment(passedDate).startOf('month');
    while (this.weekday !== date.isoWeekday()) { date.add(1, 'days'); }
    date.add(this.week - 1, 'weeks');
    return date;
  }


  hasCollidingIntervalWithShift(otherShift) {
    const diff = this.startsOn.diff(otherShift.startsOn, 'days');
    return diff % this.interval === 0 || diff % otherShift.interval === 0;
  }

  incrementDate(date) {
    let nextOccurrence = this.occurrenceInMonth(date);
    if (nextOccurrence <= date) { nextOccurrence = this.occurrenceInMonth(moment(date).add(1, 'months')); }
    while (nextOccurrence > date) { date.add(1, 'days'); }
  }
}

export function ShiftFactory(rule) {
  if (rule.options.bysetpos) { return new ShiftByPosition(rule); }
  if (rule.options.bymonthday.length > 0) { return new ShiftByMonthDay(rule); }
  if (rule.options.bynweekday) { return new ShiftByWeekNumber(rule); }
  if (rule.options.byweekday) { return new ShiftByWeekday(rule); }
  return new ShiftByDay(rule);
}
